/**
 * 로그 attribute 를 OpenTelemetry 가 받아들이는 형태로 정규화한다.
 *
 * OTel span/log attribute 값은 `string | number | boolean` 또는 동일 원시 타입
 * 배열만 허용한다. 객체·객체 배열·중첩 구조를 그대로 넘기면 HyperDX(OTel JS SDK)
 * 경로에서 **조용히 drop** 되어 ClickHouse 에는 빈 값(`[]`)만 남는다. (실측: 30일간
 * `[RTC]_load_media_devices` 13만 건 중 객체 배열 라벨이 적재된 건 1건)
 *
 * 호출부가 실수로 객체/배열을 넘겨도 데이터가 사라지지 않도록, 비-OTel-safe 값은
 * JSON 문자열로 직렬화한다. 개발 모드에서는 평탄화를 유도하는 경고를 남긴다.
 */

type Primitive = string | number | boolean;

const isPrimitive = (value: unknown): value is Primitive =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

/**
 * OTel 이 변환 없이 그대로 받는 값인지 (스칼라 / null / 동일 원시타입 배열).
 *
 * OTel 은 배열을 동일 원시 타입(string[]/number[]/boolean[])만 허용한다.
 * `['camera', 1]` 처럼 타입이 섞인 배열은 SDK 경로에서 drop 되므로 OTel-safe 가 아니다.
 */
const isOtelSafe = (value: unknown): boolean => {
  if (value == null) return true; // null/undefined 는 sink 가 알아서 누락 처리
  if (isPrimitive(value)) return true;
  if (Array.isArray(value)) {
    let elementType: 'string' | 'number' | 'boolean' | null = null;
    for (const item of value) {
      if (item == null) continue; // null 원소는 타입 판정에서 제외
      if (!isPrimitive(item)) return false; // 객체 원소 → unsafe
      if (elementType === null) {
        elementType = typeof item as 'string' | 'number' | 'boolean';
      } else if (typeof item !== elementType) {
        return false; // 혼합 원시타입 → unsafe
      }
    }
    return true;
  }
  return false;
};

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return '[Unserializable]';
  }
};

/**
 * params 중 OTel-safe 하지 않은 값만 JSON 문자열로 변환해 새 객체로 반환한다.
 * 전부 안전하면 원본 객체를 그대로 반환한다 (불필요한 복사 방지).
 */
export const normalizeLogAttributes = (
  params: Record<string, unknown>,
): Record<string, unknown> => {
  let normalized: Record<string, unknown> | null = null;

  for (const key in params) {
    const value = params[key];
    if (isOtelSafe(value)) continue;

    if (!normalized) normalized = { ...params };
    normalized[key] = safeStringify(value);

    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[ZEPLogger] attribute "${key}" is an object/array-of-objects, which OTel ` +
          `drops silently. Auto-serialized to a JSON string — flatten to scalars ` +
          `(boolean/number/string) for queryability in ClickHouse.`,
      );
    }
  }

  return normalized ?? params;
};
